/**
 * Dock slot: a bottom-right area where features dock small panels (later: message pop-outs,
 * upload progress). The shell renders one <Dock/>; features call `useDock().open(...)`.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

type DockItem = { id: string; node: ReactNode };
type DockApi = { open(id: string, node: ReactNode): void; close(id: string): void; items: DockItem[] };

const DockContext = createContext<DockApi | null>(null);

export function DockProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<DockItem[]>([]);
  const open = useCallback((id: string, node: ReactNode) => {
    setItems((prev) => [...prev.filter((i) => i.id !== id), { id, node }]);
  }, []);
  const close = useCallback((id: string) => setItems((prev) => prev.filter((i) => i.id !== id)), []);
  const api = useMemo(() => ({ open, close, items }), [open, close, items]);
  return <DockContext.Provider value={api}>{children}</DockContext.Provider>;
}

export function useDock(): DockApi {
  const api = useContext(DockContext);
  if (!api) throw new Error('useDock outside <DockProvider>');
  return api;
}

export function Dock() {
  const api = useContext(DockContext);
  if (!api || api.items.length === 0) return null;
  return (
    <div
      data-slot="dock"
      className="fixed bottom-4 right-4 z-30 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-2"
    >
      {api.items.map((i) => (
        <div key={i.id}>{i.node}</div>
      ))}
    </div>
  );
}
